import React, { useEffect, useState } from 'react';
import 'moment/locale/it';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { useArticles } from '../../../hooks/Articles/useArticles';
import { UIModal, UIModalBody, UIModalButtons, UIModalHeader, UiModalInternal } from '../../utilities/Modal';
import { UICheckbox, UIInput, UIInputLabel, UIInputTags } from '../../utilities/Input';
import { UIFormControlBox } from '../../utilities/Form';
import { UIHtmlEditor } from '../../utilities/Editor';
import { UIButton } from '../../utilities/Button';
import { IconCheck, IconTrash } from '@tabler/icons-react';
import { Box, Image } from '@chakra-ui/react';
import DropzoneFormikInput from '../../utilities/Dropzone';
import UITypography from '../../utilities/Typography';
import { UiDialog } from '../../utilities/Dialog';
import { Typography } from '@mui/material';
import { isArray } from '@apollo/client/utilities';

const ModalEdit = ({ modalOpen, handleModalClose, article }) => {
  let [articleData, setArticleData] = useState(null);
  const [deleteMode, setDeleteMode] = useState(false);
  const [uuid, setUuid] = React.useState(uuidv4());
  const [contentText, setContentText] = useState('');
  const [mobileContentText, setMobileContentText] = useState('');
  const [articleMetadata, setArticleMetadata] = useState([]);
  const [articleContents, setArticleContents] = useState([]);
  const [articleTags, setArticleTags] = useState([]);
  const [articleTagsOptions, setArticleTagsOptions] = useState([]);

  useEffect(() => {
    setUuid(uuidv4());
  }, [article]);

  const { onGetArticleAdmin, onUpdateArticleAdmin, onCreateArticleAdmin, onDeleteArticleAdmin, onListArticleTagsAdmin } = useArticles();

  const getArticle = async () => {
    onGetArticleAdmin({
      article_uuid: article.uuid
    }).then((res) => {
      setArticleData(res);
    });
  };

  const getArticleTags = async () => {
    onListArticleTagsAdmin().then((res) => {
      setArticleTagsOptions(res.map((tag) => ({ name: tag.name, id: tag.id })));
    });
  };

  useEffect(() => {
    if (!article) return;
    getArticle().then(() => {});
  }, [article]);

  useEffect(() => {
    getArticleTags().then(() => {});
  }, [modalOpen]);

  useEffect(() => {
    if (!articleData) return;
    setContentText(articleData?.text);
    setMobileContentText(articleData?.mobile_content);
    setArticleMetadata(articleData?.metadata.map((m) => ({ name: m.name, content: m.content })));
    setArticleContents(articleData?.contents?.map((c) => ({ title: c.title, text: c.text })) || []);
    setArticleTags(articleData?.tags?.map((t) => ({ name: t.name, id: t.id })) || []);
  }, [articleData]);

  const handleSubmit = async (values) => {
    const { title, slug, cover, preview, active, fixed } = values;

    if (articleData?.uuid) {
      onUpdateArticleAdmin({
        article_uuid: articleData?.uuid,
        article_data: {
          title,
          slug,
          content: contentText,
          contents: articleContents,
          mobile_content: mobileContentText,
          cover,
          preview,
          tags: articleTags,
          fixed,
          active,
          metadata: articleMetadata
        }
      }).then((res) => {
        toast(res?.response, {
          style: {
            fontSize: '14px',
            backgroundColor: res?.responseStatus ? '#00e676' : '#FF646C',
            color: '#ffffff'
          }
        });
        if (res?.responseStatus) {
          handleModalCloseInternal();
        }
      });
    } else {
      onCreateArticleAdmin({
        article_data: {
          uuid: uuid,
          title,
          slug,
          content: contentText,
          contents: articleContents,
          mobile_content: mobileContentText,
          cover,
          preview,
          tags: articleTags,
          fixed,
          active,
          metadata: articleMetadata
        }
      }).then((res) => {
        toast(res?.response, {
          style: {
            fontSize: '14px',
            backgroundColor: res?.responseStatus ? '#00e676' : '#FF646C',
            color: '#ffffff'
          }
        });
        if (res?.responseStatus) {
          handleModalCloseInternal();
        }
      });
    }
  };

  const handleDeleteArticle = () => {
    onDeleteArticleAdmin({
      article_uuid: articleData?.uuid
    }).then((res) => {
      if (res?.response) {
        toast(res?.response, {
          style: {
            fontSize: '14px',
            backgroundColor: '#00e676',
            color: '#ffffff'
          }
        });
      }
      handleModalCloseInternal();
    });
  };

  const handleModalCloseInternal = () => {
    setArticleData(null);
    setDeleteMode(false);
    setMobileContentText('');
    setContentText('');
    setArticleMetadata([]);
    setArticleContents([]);
    setArticleTags([]);
    handleModalClose();
  };

  const handleMetadataUpdate = (metadata, index, field) => {
    const newMetadata = [...articleMetadata];
    newMetadata[index][field] = metadata;
    setArticleMetadata(newMetadata);
  };

  const handleMetadataAdd = () => {
    const newMetadata = [...articleMetadata];
    newMetadata.push({ name: '', content: '' });
    setArticleMetadata(newMetadata);
  };

  const handleMetadataRemove = (index) => {
    const newMetadata = [...articleMetadata];
    newMetadata.splice(index, 1);
    setArticleMetadata(newMetadata);
  };

  const handleContentsUpdate = (content, index, field, setFieldValue) => {
    const newContents = [...articleContents];
    newContents[index][field] = content;
    setFieldValue('contents', newContents);
    setArticleContents(newContents);
  };

  const handleContentsAdd = (setFieldValue) => {
    const newContents = [...articleContents];
    newContents.push({ title: '', text: '' });
    setFieldValue('contents', newContents);
    setArticleContents(newContents);
  };

  const handleContentsRemove = (index, setFieldValue) => {
    const newContents = [...articleContents];
    newContents.splice(index, 1);
    setFieldValue('contents', newContents);
    setArticleContents(newContents);
  };

  return (
    <UIModal open={modalOpen} onClose={handleModalCloseInternal} allowFullScreen={true} className={'modal'}>
      <UiDialog
        type={'default'}
        variant={'cancel'}
        title={"Vuoi eliminare definitivamente l'articolo?"}
        open={deleteMode}
        onClose={() => setDeleteMode(false)}
        onConfirm={handleDeleteArticle}
      />

      <Formik
        initialValues={{
          title: articleData?.title || '',
          slug: articleData?.slug || '',
          content: articleData?.text || '',
          contents: articleData?.contents || '',
          mobile_content: articleData?.mobile_content || '',
          cover: articleData?.cover || '',
          preview: articleData?.preview || '',
          tags: articleData?.tags || '',
          fixed: articleData?.fixed || false,
          active: articleData?.active
        }}
        validationSchema={Yup.object().shape({
          title: Yup.string().required('Inserisci un titolo'),
          slug: Yup.string().required('Inserisci uno slug univoco'),
          content: Yup.string().test({
            test: () => {
              return contentText?.length > 150;
            },
            message: 'Inserisci almeno 150 caratteri'
          }),
          // contents: Yup.array()
          //   .of(
          //     Yup.object({
          //       title: Yup.string().required('Il titolo è obbligatorio'),
          //       text: Yup.string().required('Il testo è obbligatorio')
          //     })
          //   )
          //   .min(1, 'Inserisci almeno una sezione di contenuto')
          //   .required('Inserisci almeno una sezione di contenuto'),
          mobile_content: Yup.string().test({
            test: () => {
              return mobileContentText?.length > 150;
            },
            message: 'Inserisci almeno 150 caratteri'
          }),
          cover: Yup.string().required("Inserisci un'immagine"),
          preview: Yup.string().required("Inserisci un'immagine"),
          tags: Yup.array().min(1, 'Inserisci almeno un tag').required('Inserisci almeno un tag'),
          fixed: Yup.boolean(),
          active: Yup.boolean()
        })}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ errors, submitForm, handleSubmit, touched, values, setFieldValue }) => (
          <form onSubmit={handleSubmit} id={'article-form'}>
            <UiModalInternal
              onClose={handleModalCloseInternal}
              isLoading={article && articleData === null}
              containerSx={{
                width: '80%',
                maxWidth: '800px'
              }}
            >
              <UIModalHeader sx={{ border: 'none', p: '50px 0 0 0' }} />
              <UIModalBody
                sx={{
                  bgcolor: 'transparent'
                }}
              >
                <UIFormControlBox label={'Titolo'} error={Boolean(touched.title && errors.title)} error_message={errors?.title}>
                  <UIInput value={values.title} onChange={(e) => setFieldValue('title', e.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox label={'Slug'} error={Boolean(touched.slug && errors.slug)} error_message={errors?.slug}>
                  <UIInput value={values.slug} onChange={(e) => setFieldValue('slug', e.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox
                  label={'Contenuto Web'}
                  subtitle={'Contenuto visualizzato su desktop.'}
                  error={Boolean(touched.content && errors.content)}
                  error_message={errors?.content}
                >
                  <UIHtmlEditor
                    content={values?.content}
                    onChange={(html) => {
                      setContentText(html);
                      setFieldValue('content', html);
                    }}
                  />
                </UIFormControlBox>

                <UIFormControlBox label={'Sezioni'} error_message={errors?.contents}>
                  {articleContents?.map((content, index) => (
                    <Box
                      key={index}
                      display={'flex'}
                      flexWrap={'wrap'}
                      gap={'20px'}
                      mt={10}
                      sx={{
                        border: '1px solid #000',
                        borderRadius: '8px',
                        padding: '10px'
                      }}
                    >
                      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
                        <Typography marginLeft={'5px'} fontFamily={'Oxygen'} fontWeight={'700'} fontSize={'14px'}>
                          Sezione {index + 1}
                        </Typography>
                        <UIButton
                          sxCustom={{
                            bgcolor: '#FF646C',
                            color: 'white',
                            padding: '5px',
                            minWidth: 'auto',
                            marginLeft: '10px'
                          }}
                          onClick={() => handleContentsRemove(index, setFieldValue)}
                          icon={<IconTrash stroke={1.64} size="15px" />}
                        />
                      </Box>
                      <UIFormControlBox
                        sx={{ display: 'block', width: '100%', p: 0 }}
                        error={Boolean(touched?.contents?.[index]?.title && errors.contents?.[index]?.title)}
                        error_message={errors.contents?.[index]?.title}
                      >
                        <UIInputLabel label={'Titolo'} htmlFor={'test'} />
                        <UIInput
                          label={'Titolo'}
                          value={content?.title}
                          aria-describedby="base-name-helper-text"
                          onChange={(e) => handleContentsUpdate(e.target.value, index, 'title', setFieldValue)}
                          sx={{ color: 'red' }}
                        />
                      </UIFormControlBox>
                      <UIFormControlBox
                        sx={{ display: 'block', width: '100%', p: 0 }}
                        label={'Contenuto'}
                        error={Boolean(touched?.contents?.[index]?.text && errors.contents?.[index]?.text)}
                        error_message={errors.contents?.[index]?.text}
                      >
                        <UIInputLabel label={'Content'} htmlFor={'test1'} />
                        <UIHtmlEditor
                          content={content?.text}
                          onChange={(html) => handleContentsUpdate(html, index, 'text', setFieldValue)}
                        />
                      </UIFormControlBox>
                    </Box>
                  ))}

                  <Box mt={10}>
                    <UIButton
                      onClick={() => {
                        handleContentsAdd(setFieldValue);
                      }}
                      label={'Aggiungi sezione'}
                    />
                    {!isArray(errors?.contents) && (
                      <UITypography
                        variant={'Oxygen300'}
                        sxCustom={{
                          color: '#FF646C',
                          fontSize: '12px',
                          marginTop: '10px',
                          marginLeft: '10px'
                        }}
                        title={errors?.contents}
                      />
                    )}
                  </Box>
                </UIFormControlBox>

                <UIFormControlBox
                  label={'Contenuto Mobile'}
                  subtitle={'Contenuto visualizzato su cellulare.'}
                  error={Boolean(touched.mobile_content && errors.mobile_content)}
                  error_message={errors?.mobile_content}
                >
                  <UIHtmlEditor content={mobileContentText} onChange={(html) => setMobileContentText(html)} />
                </UIFormControlBox>

                <UIFormControlBox label={'Metadata'} error_message={errors?.metadata}>
                  {articleMetadata?.map((metadata, index) => (
                    <Box key={index} display={'flex'} flexWrap={'wrap'} gap={'20px'} mt={10}>
                      <UIFormControlBox sx={{ display: 'block', width: 'auto', p: 0 }}>
                        <UIInputLabel label={'Name'} htmlFor={'test'} />
                        <UIInput
                          id={'test'}
                          label={'Name'}
                          value={metadata?.name}
                          aria-describedby="base-name-helper-text"
                          onChange={(e) => handleMetadataUpdate(e.target.value, index, 'name')}
                          sx={{ color: 'red' }}
                        />
                      </UIFormControlBox>
                      <UIFormControlBox sx={{ display: 'block', width: 'auto', p: 0 }}>
                        <UIInputLabel label={'Content'} htmlFor={'test1'} />
                        <UIInput
                          id={'test1'}
                          label={'Content'}
                          value={metadata?.content}
                          aria-describedby="base-name-helper-text"
                          onChange={(e) => handleMetadataUpdate(e.target.value, index, 'content')}
                          sx={{ color: 'red' }}
                        />
                      </UIFormControlBox>
                      <UIButton
                        sxCustom={{
                          bgcolor: '#FF646C',
                          color: 'white',
                          padding: '10px',
                          minWidth: 'auto'
                        }}
                        onClick={() => handleMetadataRemove(index)}
                        icon={<IconTrash stroke={1.64} size="25px" />}
                      />
                    </Box>
                  ))}

                  <Box mt={10}>
                    <UIButton onClick={handleMetadataAdd} label={'Aggiungi Metadata'} />
                  </Box>
                </UIFormControlBox>

                <UIFormControlBox label={'Immagine'} error={Boolean(touched.cover && errors.cover)} error_message={errors?.cover}>
                  <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={'0 10px'}>
                    <Image src={values.cover} w={'100px'} h={'100px'} objectFit={'cover'} />
                    <DropzoneFormikInput
                      variant={'article_cover'}
                      setFieldValue={setFieldValue}
                      field={'cover'}
                      folder={'articles'}
                      label={'Carica'}
                      extra_data={{
                        article_uuid: articleData?.uuid ?? uuid
                      }}
                    />
                  </Box>
                </UIFormControlBox>

                <UIFormControlBox label={'Immagine'} error={Boolean(touched.preview && errors.preview)} error_message={errors?.preview}>
                  <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={'0 10px'}>
                    <Image src={values.preview} w={'100px'} h={'100px'} objectFit={'cover'} />
                    <DropzoneFormikInput
                      variant={'article_preview'}
                      setFieldValue={setFieldValue}
                      field={'preview'}
                      folder={'articles'}
                      label={'Carica'}
                      extra_data={{
                        article_uuid: articleData?.uuid ?? uuid
                      }}
                    />
                  </Box>
                </UIFormControlBox>

                <UIFormControlBox label={'Tags'} error={Boolean(touched.tags && errors.tags)} error_message={errors?.tags}>
                  <UIInputTags
                    options={articleTagsOptions}
                    filterOption={(opts) => {
                      return opts;
                    }}
                    value={articleTags}
                    onChange={(event, newValue, reason) => {
                      if (reason === 'createOption') {
                        const last = newValue[newValue.length - 1];
                        const newOption = { name: last, id: 'NEW_TAG_' + uuidv4() };
                        setArticleTagsOptions([...articleTagsOptions, newOption]);
                        setArticleTags([...articleTags, newOption]);
                        setFieldValue('tags', [...articleTags, newOption]);
                      } else if (reason === 'selectOption') {
                        setArticleTags(newValue);
                        setFieldValue('tags', newValue);
                      } else if (reason === 'removeOption') {
                        setArticleTags(newValue);
                        setFieldValue('tags', newValue);
                      }
                    }}
                  />
                </UIFormControlBox>

                <UIFormControlBox>
                  <UICheckbox
                    label={<UITypography title={'In primo piano'} />}
                    name={'fixed'}
                    readOnly={true}
                    checked={values?.fixed}
                    onChange={(e) => setFieldValue('fixed', e.target.checked)}
                  />
                </UIFormControlBox>

                <UIFormControlBox>
                  <UICheckbox
                    label={<UITypography title={'Attivo'} />}
                    name={'active'}
                    readOnly={true}
                    checked={values?.active}
                    onChange={(e) => setFieldValue('active', e.target.checked)}
                  />
                </UIFormControlBox>
              </UIModalBody>

              <UIModalButtons>
                <UIButton type={'submit'} onClick={() => submitForm()} icon={<IconCheck stroke={'1.64'} size={'26px'} />} />
                <UIButton
                  type={'submit'}
                  onClick={() => {
                    setDeleteMode(true);
                  }}
                  icon={<IconTrash stroke={1.64} size="25px" />}
                  sxCustom={{
                    bgcolor: '#FF646C'
                  }}
                />
              </UIModalButtons>
            </UiModalInternal>
          </form>
        )}
      </Formik>
    </UIModal>
  );
};

export default ModalEdit;
